import { useState } from "react";
import { useGetGridSymbolOrdersQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { GridSymbolChart } from "@/components/domain/GridSymbolChart";

interface Props {
  symbol: string;           // e.g. "BTC/USDT"
  regime?: any;             // from /api/grid/status by_symbol[sym].regime
  fillsSummary?: any;
  defaultExpanded?: boolean;
}

/**
 * GridSymbolPanel — one expandable card per grid symbol. Header shows
 * the high-level state (regime, run, P&L). When expanded:
 *   - Recharts chart (price + grid levels + live + fills)
 *   - Two tables side-by-side: open orders / executed fills
 *
 * Open orders are sorted by price DESC so the visual stack matches the
 * chart. Fills are sorted DESC by time so the most recent is on top.
 */
export function GridSymbolPanel({ symbol, regime, fillsSummary, defaultExpanded }: Props) {
  const [expanded, setExpanded] = useState(!!defaultExpanded);
  const base = symbol.split("/")[0];
  const q = useGetGridSymbolOrdersQuery(
    { base, fills_limit: 200 },
    { pollingInterval: 15000, skip: !expanded },
  );

  const verdict = regime?.verdict || "?";
  const tone = verdict === "grid_friendly" ? "border-emerald-500/40 bg-emerald-500/5"
    : verdict === "disable" ? "border-rose-500/30 bg-rose-500/5"
    : "border-amber-500/30 bg-amber-500/5";

  const realized = Number(fillsSummary?.realized_pnl ?? 0);
  const fillsN = Number(fillsSummary?.n ?? 0);

  return (
    <Card className={`border ${tone}`}>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setExpanded((e) => !e)}
      >
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span className="font-mono">{symbol}</span>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone={verdict === "grid_friendly" ? "good" : verdict === "disable" ? "bad" : "warn"}>
              {verdict} · {regime?.score ?? "?"}
            </Badge>
            <span className="text-sm text-muted font-normal">
              ${Number(regime?.current_price ?? 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}
            </span>
            <span className="text-sm font-normal">
              {fillsN} fills ·{" "}
              <span className={realized > 0 ? "text-emerald-400" : realized < 0 ? "text-rose-400" : ""}>
                ${realized.toFixed(2)}
              </span>
            </span>
            <span className="text-xs text-muted">{expanded ? "▼" : "▶"}</span>
          </div>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-3">
          <GridSymbolChart base={base} height={280} />

          {q.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <OpenOrders rows={q.data?.open || []} />
              <ExecutedFills rows={q.data?.fills || []} />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function OpenOrders({ rows }: { rows: any[] }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted mb-1">
        Open orders <span className="text-fg/80">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted">No open orders.</div>
      ) : (
        <div className="overflow-x-auto max-h-72 overflow-y-auto border border-border rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-bg2">
              <tr className="text-left text-muted">
                <th className="py-1 px-2">Side</th>
                <th className="py-1 px-2">Lvl</th>
                <th className="py-1 px-2 text-right">Price</th>
                <th className="py-1 px-2 text-right">Qty</th>
                <th className="py-1 px-2 text-right">Notional</th>
                <th className="py-1 px-2">Order ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-border/40">
                  <td className="py-1 px-2">
                    <span className={o.side === "buy" ? "text-emerald-400" : "text-rose-400"}>
                      {o.side}
                    </span>
                  </td>
                  <td className="py-1 px-2 text-muted">{o.grid_level}</td>
                  <td className="py-1 px-2 text-right font-mono">${Number(o.price).toFixed(4)}</td>
                  <td className="py-1 px-2 text-right font-mono">{Number(o.qty).toFixed(8)}</td>
                  <td className="py-1 px-2 text-right font-mono">${(Number(o.price) * Number(o.qty)).toFixed(2)}</td>
                  <td className="py-1 px-2 text-muted font-mono truncate" title={o.order_id}>
                    {o.order_id?.slice(0, 12) || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ExecutedFills({ rows }: { rows: any[] }) {
  const realizedTotal = rows.reduce((a, r) => a + Number(r.pnl_usd || 0), 0);
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted mb-1 flex items-center justify-between">
        <span>Executed fills <span className="text-fg/80">({rows.length})</span></span>
        <span className={realizedTotal > 0 ? "text-emerald-400" : realizedTotal < 0 ? "text-rose-400" : ""}>
          ${realizedTotal.toFixed(2)} realized
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted">No fills yet.</div>
      ) : (
        <div className="overflow-x-auto max-h-72 overflow-y-auto border border-border rounded">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-bg2">
              <tr className="text-left text-muted">
                <th className="py-1 px-2">Time</th>
                <th className="py-1 px-2">Side</th>
                <th className="py-1 px-2">Lvl</th>
                <th className="py-1 px-2 text-right">Price</th>
                <th className="py-1 px-2 text-right">Qty</th>
                <th className="py-1 px-2 text-right">P&L</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.id} className="border-t border-border/40">
                  <td className="py-1 px-2 text-muted font-mono">
                    {(f.ts || "").slice(5, 19).replace("T", " ")}
                  </td>
                  <td className="py-1 px-2">
                    <span className={f.side === "buy" ? "text-emerald-400" : "text-rose-400"}>
                      {f.side}
                    </span>
                  </td>
                  <td className="py-1 px-2 text-muted">{f.grid_level}</td>
                  <td className="py-1 px-2 text-right font-mono">${Number(f.price).toFixed(4)}</td>
                  <td className="py-1 px-2 text-right font-mono">{Number(f.qty).toFixed(8)}</td>
                  <td className={`py-1 px-2 text-right font-mono ${(Number(f.pnl_usd) || 0) > 0 ? "text-emerald-400" : (Number(f.pnl_usd) || 0) < 0 ? "text-rose-400" : "text-muted"}`}>
                    {f.pnl_usd != null ? `$${Number(f.pnl_usd).toFixed(4)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
